import { __awaiter } from "tslib";
import { ButtonType, ParagraphSize, MessageType } from '@getaccept/fried-tofu';
import { AnalyticsEvent, AnalyticsUserTrait } from '@getaccept/lib-shared-new/src/types/analytics';
import { sanitizeEmail } from '@getaccept/lib-shared-new/src/helpers';
import { validateEmail } from '@getaccept/lib-shared/src/helpers';
import LoginSignupContainer from '@getaccept/lib-shared-new/src/login-signup/components/LoginSignupContainer.vue';
import Analytics from '@getaccept/lib-shared-new/src/analytics';
import { Language } from '@getaccept/lib-shared-new/src/languages/enums/language';
import { RecaptchaAction } from '@getaccept/lib-shared-new/src/recaptcha/recaptcha-action';
import { onMounted, onUnmounted, defineComponent, computed, ref, watch } from 'vue';
import { LocalStorageHelper } from '@getaccept/lib-shared-new/src/helpers/local-storage.helper';
import { LocalStorageKey } from '@getaccept/lib-shared-new/src/enums/local-storage';
import { useI18n } from 'vue-i18n';
import GoogleIcon from '../assets/google.svg';
import LinkedInIcon from '../assets/linkedin.svg';
import { useLanguageStore } from '../../language/store/language.store';
import { IdentityProvider } from '../types/enums/identity-provider';
import { IdentityProviderHelper } from '../../helpers/identity-provider.helper';
import LocationService from '../../location/location.service';
import { SignupHelper } from '../../helpers/signup.helper';
import { RecaptchaService } from '../../api/recaptcha/recaptcha.service';
import { getUTMData } from '../../helpers/get-utm-data.helper';
export default defineComponent({
    name: 'Signup',
    components: {
        LoginSignupContainer,
        GoogleIcon,
        LinkedInIcon,
    },
    props: {
        templateId: { type: String, default: null },
        isModal: { type: Boolean, default: false },
        title: { type: String, default: 'welcome-to-getaccept' },
        buttonText: { type: String, default: 'sign-up' },
        registrationSource: { type: String, default: 'signup' },
        registrationCampaignId: { type: String, default: null },
        registrationEmail: { type: String, default: '' },
    },
    setup(props) {
        const { t } = useI18n();
        const email = ref('');
        const emailValidation = ref('');
        const loading = ref(false);
        const error = ref(null);
        const signupFormRef = ref({ id: 'signup-form' });
        const languageStore = ref(useLanguageStore());
        watch(email, () => {
            emailValidation.value = '';
        });
        const didYouMean = computed(() => { var _a, _b; return ((_b = (_a = error.value) === null || _a === void 0 ? void 0 : _a.variables) === null || _b === void 0 ? void 0 : _b.didYouMean) || ''; });
        const getEmailValidation = (email) => {
            if (!email) {
                return t('email-is-required').toString();
            }
            else if (!validateEmail(sanitizeEmail(email))) {
                return t('enter-a-valid-email').toString();
            }
            return '';
        };
        const redirectToLogin = () => {
            window.location.href = '/';
        };
        const handleDidYouMeanClick = () => {
            var _a;
            email.value = (_a = error.value) === null || _a === void 0 ? void 0 : _a.variables.didYouMean;
            error.value = null;
        };
        const handleSubmit = () => __awaiter(this, void 0, void 0, function* () {
            error.value = null;
            const cleanEmail = sanitizeEmail(email.value);
            emailValidation.value = getEmailValidation(cleanEmail);
            const geoLocation = yield LocationService.getGeoLocation();
            if (!emailValidation.value) {
                const analyticsData = {
                    [AnalyticsUserTrait.SignupMethod]: 'email',
                    [AnalyticsUserTrait.Email]: cleanEmail,
                    [AnalyticsUserTrait.SignupVersion]: 1,
                    [AnalyticsUserTrait.SignupStep]: 1,
                    [AnalyticsUserTrait.Country]: geoLocation.country_name,
                };
                const currentSignupData = JSON.parse(LocalStorageHelper.getLocalStorage(LocalStorageKey.SignupAnalyticsData));
                LocalStorageHelper.setLocalStorage(LocalStorageKey.SignupAnalyticsData, JSON.stringify(Object.assign(Object.assign({}, currentSignupData), analyticsData)));
                Analytics.identify(undefined, analyticsData);
                Analytics.track(AnalyticsEvent.SignupInitiated, analyticsData);
                const recaptchaToken = yield RecaptchaService.getToken(RecaptchaAction.Signup);
                loading.value = true;
                error.value = yield SignupHelper.signup({
                    email: cleanEmail,
                    registrationSource: props.registrationSource,
                    templateId: props.templateId,
                    registrationCampaign: props.registrationCampaignId,
                    language: languageStore.value.language,
                    recaptchaToken,
                });
                loading.value = false;
            }
        });
        const handleSignupAnalytics = () => {
            const utmData = getUTMData();
            const analyticsData = Object.assign({ [AnalyticsUserTrait.SignupVersion]: 1, [AnalyticsUserTrait.Browser]: navigator.userAgent, [AnalyticsUserTrait.SignupStep]: 0 }, utmData);
            LocalStorageHelper.setLocalStorage(LocalStorageKey.SignupAnalyticsData, JSON.stringify(analyticsData));
            Analytics.identify(undefined, analyticsData);
            Analytics.track(AnalyticsEvent.SignupLoaded, analyticsData);
        };
        const validatePostMessage = (event) => __awaiter(this, void 0, void 0, function* () {
            const url = IdentityProviderHelper.getMessageUrl();
            if (!IdentityProviderHelper.validateMessageAndOrigin(event, url)) {
                return;
            }
            loading.value = true;
            yield SignupHelper.validateIdentityProviderMessage({ event, url });
            loading.value = false;
        });
        const handleProviderSignup = (provider) => __awaiter(this, void 0, void 0, function* () {
            const geoLocation = yield LocationService.getGeoLocation();
            const analyticsData = {
                [AnalyticsUserTrait.SignupMethod]: provider,
                [AnalyticsUserTrait.SignupVersion]: 1,
                [AnalyticsUserTrait.SignupStep]: 1,
                [AnalyticsUserTrait.Country]: geoLocation.country_name,
            };
            const currentSignupData = JSON.parse(LocalStorageHelper.getLocalStorage(LocalStorageKey.SignupAnalyticsData));
            LocalStorageHelper.setLocalStorage(LocalStorageKey.SignupAnalyticsData, JSON.stringify(Object.assign(Object.assign({}, currentSignupData), analyticsData)));
            Analytics.identify(undefined, analyticsData);
            Analytics.track(AnalyticsEvent.SignupInitiated, analyticsData);
            SignupHelper.launchIdentifier({
                identityProvider: provider,
                templateId: props.templateId,
                registrationCampaignId: props.registrationCampaignId,
                language: languageStore.value.language,
            });
        });
        onMounted(() => {
            handleSignupAnalytics();
            window.addEventListener('message', validatePostMessage);
            if (languageStore.value.language !== Language.En) {
                signupFormRef.value.id = `signup-form-${languageStore.value.language}`;
            }
            if (props.registrationEmail) {
                try {
                    email.value = atob(props.registrationEmail);
                }
                catch (e) {
                    // Do nothing
                }
            }
        });
        onUnmounted(() => {
            window.removeEventListener('message', validatePostMessage);
        });
        return {
            IdentityProvider,
            ButtonType,
            ParagraphSize,
            MessageType,
            email,
            emailValidation,
            loading,
            error,
            didYouMean,
            signupFormRef,
            handleSignupAnalytics,
            redirectToLogin,
            handleDidYouMeanClick,
            handleSubmit,
            getEmailValidation,
            handleProviderSignup,
        };
    },
});
